import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { billingService } from './billingService';
import type { AdminUser } from './adminService';

type InvoiceLike = {
  status?: string;
  amount_paid?: number;
  created?: number;
};

export type OverviewAccurateMetrics = {
  revenueToday: number;
  revenueThisMonth: number;
  revenueLastMonth: number;
  totalRevenue: number;
  dailyRevenueLast7Days: number[];
  emailsSentToday: number;
  emailsSentThisMonth: number;
  emailsSentThisWeek: number;
  totalEmailsSent: number;
  dailyEmailsLast7Days: number[];
  totalUsers: number;
  activeUsers: number;
  paidUsers: number;
  newUsersThisMonth: number;
  newUsersLastMonth: number;
  userGrowthRate: number;
};

function isPaidInvoice(inv: InvoiceLike): boolean {
  const s = String(inv.status || '').toLowerCase();
  return s === 'paid' || s === 'succeeded' || s === 'success';
}

function invoiceAmountUsd(inv: InvoiceLike): number {
  return (inv.amount_paid || 0) / 100;
}

/** Revenue from Firestore `invoices` (same source as Admin Revenue page). */
export function computeRevenueStatsFromInvoices(invoices: InvoiceLike[]) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();

  let revenueToday = 0;
  let revenueThisMonth = 0;
  let revenueLastMonth = 0;
  let totalRevenue = 0;
  const dailyRevenueLast7Days = [0, 0, 0, 0, 0, 0, 0];

  for (const inv of invoices.filter(isPaidInvoice)) {
    const amt = invoiceAmountUsd(inv);
    const ts = (inv.created || 0) * 1000;
    if (!ts) continue;

    totalRevenue += amt;
    if (ts >= monthStart) revenueThisMonth += amt;
    if (ts >= lastMonthStart && ts < monthStart) revenueLastMonth += amt;
    if (ts >= startOfToday) revenueToday += amt;

    for (let i = 6; i >= 0; i--) {
      const dayStart = new Date(now);
      dayStart.setDate(dayStart.getDate() - i);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);
      if (ts >= dayStart.getTime() && ts < dayEnd.getTime()) {
        dailyRevenueLast7Days[6 - i] += amt;
        break;
      }
    }
  }

  return {
    revenueToday: Math.round(revenueToday * 100) / 100,
    revenueThisMonth: Math.round(revenueThisMonth),
    revenueLastMonth: Math.round(revenueLastMonth),
    totalRevenue: Math.round(totalRevenue),
    dailyRevenueLast7Days,
  };
}

/** User counts from deduped-by-email list (overview uses unique accounts, not raw profile count). */
export function computeUserMetricsFromList(users: AdminUser[]) {
  const now = Date.now();
  const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const lastMonthStart = new Date(monthStart);
  lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);

  let newUsersThisMonth = 0;
  let newUsersLastMonth = 0;
  let activeUsers = 0;
  let paidUsers = 0;

  for (const u of users) {
    const createdMs =
      u.createdAt?.toDate?.()?.getTime() ??
      (u.createdAt?.seconds != null ? u.createdAt.seconds * 1000 : 0);
    if (createdMs >= monthStart.getTime()) newUsersThisMonth++;
    if (createdMs >= lastMonthStart.getTime() && createdMs < monthStart.getTime()) {
      newUsersLastMonth++;
    }

    const lastLoginMs =
      u.lastLoginAt?.toDate?.()?.getTime() ??
      (u.lastLoginAt?.seconds != null ? u.lastLoginAt.seconds * 1000 : 0);
    if (lastLoginMs >= thirtyDaysAgo) activeUsers++;

    const sub = u.subscription as { plan?: string; status?: string } | null | undefined;
    const plan = String(sub?.plan || '').toLowerCase();
    const subStatus = String(sub?.status || '').toLowerCase();
    if (subStatus === 'active' && plan && plan !== 'starter' && plan !== 'free') {
      paidUsers++;
    }
  }

  const userGrowthRate =
    newUsersLastMonth > 0
      ? ((newUsersThisMonth - newUsersLastMonth) / newUsersLastMonth) * 100
      : newUsersThisMonth > 0
        ? 100
        : 0;

  return {
    totalUsers: users.length,
    activeUsers,
    paidUsers,
    newUsersThisMonth,
    newUsersLastMonth,
    userGrowthRate: Math.round(userGrowthRate * 10) / 10,
  };
}

/** Email volume — sourced from real SES statistics + email_logs count. Falls back to campaign scan if CF unavailable. */
export async function computeEmailStatsFromCampaigns() {
  // Try the accurate Cloud Function first (SES + email_logs)
  try {
    const getSESEmailStats = httpsCallable<unknown, {
      recommended: {
        sentToday: number;
        sentThisWeek: number;
        sentThisMonth: number;
        totalEmailsSent: number;
        dailyLast7Days: number[];
      };
      sesStatsAvailable: boolean;
    }>(functions, 'getSESEmailStats');

    const result = await getSESEmailStats({});
    const r = result.data.recommended;
    return {
      emailsSentToday:    r.sentToday,
      emailsSentThisMonth: r.sentThisMonth,
      emailsSentThisWeek:  r.sentThisWeek,
      totalEmailsSent:    r.totalEmailsSent,
      dailyEmailsLast7Days: r.dailyLast7Days,
    };
  } catch (e) {
    console.warn('getSESEmailStats CF failed, falling back to campaign scan:', (e as Error).message);
  }

  // Fallback: count from campaign documents (less accurate — misses automations + transactional)
  const snap = await getDocs(query(collection(db, 'campaigns'), where('status', '==', 'sent')));
  const campaigns = snap.docs.map((d) => d.data());

  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - 6);
  weekStart.setHours(0, 0, 0, 0);

  let emailsSentToday = 0;
  let emailsSentThisMonth = 0;
  let emailsSentThisWeek = 0;
  let totalEmailsSent = 0;
  const dailyEmailsLast7Days = [0, 0, 0, 0, 0, 0, 0];

  for (const c of campaigns) {
    const rec =
      (typeof c.statistics?.totalRecipients === 'number' && c.statistics.totalRecipients) ||
      (typeof c.statistics?.sent === 'number' && c.statistics.sent) ||
      (typeof c.recipientCount === 'number' && c.recipientCount) ||
      0;

    totalEmailsSent += rec;
    const sentAt = c.sentAt?.toDate?.() as Date | undefined;
    if (!sentAt) continue;

    if (sentAt >= startOfToday) emailsSentToday += rec;
    if (sentAt >= monthStart) emailsSentThisMonth += rec;
    if (sentAt >= weekStart) emailsSentThisWeek += rec;

    for (let i = 6; i >= 0; i--) {
      const dayStart = new Date(now);
      dayStart.setDate(dayStart.getDate() - i);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);
      if (sentAt >= dayStart && sentAt < dayEnd) {
        dailyEmailsLast7Days[6 - i] += rec;
        break;
      }
    }
  }

  return {
    emailsSentToday,
    emailsSentThisMonth,
    emailsSentThisWeek,
    totalEmailsSent,
    dailyEmailsLast7Days,
  };
}

/** Single load used by admin overview after users are fetched. */
export async function loadOverviewAccurateMetrics(users: AdminUser[]): Promise<OverviewAccurateMetrics> {
  const [invoices, emailStats] = await Promise.all([
    billingService.getAllInvoices(5000).catch(() => []),
    computeEmailStatsFromCampaigns().catch(() => ({
      emailsSentToday: 0,
      emailsSentThisMonth: 0,
      emailsSentThisWeek: 0,
      totalEmailsSent: 0,
      dailyEmailsLast7Days: [0, 0, 0, 0, 0, 0, 0],
    })),
  ]);

  const revenue = computeRevenueStatsFromInvoices(invoices);
  const userMetrics = computeUserMetricsFromList(users);

  return {
    ...revenue,
    ...emailStats,
    ...userMetrics,
  };
}
